import { apiFetch } from "../fetch-wrapper";
import { getApiUrl } from "config";
import type { Mimir } from "bildebanken-model";
import { getMetadataItem } from "services/api";

const LABEL_DETECTION_POLL_INTERVAL_MS = 3000;
const LABEL_DETECTION_MAX_ATTEMPTS = 3;

type Captions = {
  caption: {
    nb: string;
  };
};

export async function getGeneratedAltTextFromUrl(imageProxyUrl: string): Promise<Captions> {
  const response = await apiFetch(`${getApiUrl()}/images/url/caption`, {
    method: "POST",
    body: JSON.stringify({ url: imageProxyUrl }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw Error("No access to this page");
    }
    throw Error("Could not get caption for the provided image URL");
  }
  return response.json();
}

export async function startLabelDetection(itemId: string): Promise<void> {
  const response = await apiFetch(`${getApiUrl()}/images/${itemId}/tags`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw Error("No access to this page");
    }
    throw Error("Could not start label detection for image " + itemId);
  }
}

export async function detectLabels(itemId: string): Promise<Mimir.DetectedLabels> {
  await startLabelDetection(itemId);

  for (let attempt = 0; attempt < LABEL_DETECTION_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, LABEL_DETECTION_POLL_INTERVAL_MS));
    const image = await getMetadataItem(itemId);
    if (image.labelDetectionState === "complete" && image.detectedLabels) {
      return image.detectedLabels;
    }
  }

  throw Error("Label detection did not complete in time");
}

export async function getGeneratedAltTextFromBuffer(data: Blob): Promise<Captions> {
  const imageBase64 = await blobToDataUrl(data);
  const result = await apiFetch(`${getApiUrl()}/images/caption`, {
    method: "POST",
    body: JSON.stringify({ imageBase64 }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!result.ok) {
    if (result.status === 403) {
      throw Error("No access to this page");
    }
    throw Error("Could not get caption for the provided image data");
  }
  return result.json();
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const contentType = "image/jpeg";
  if (!contentType) {
    throw Error(`Expected an image but received ${blob.type || "unknown content type"}`);
  }

  const imageBlob = new Blob([bytes], { type: contentType });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Could not read image data"));
    reader.readAsDataURL(imageBlob);
  });
}
